import Translations from "@translate/utils/member/checkoutChannel";
import { MemberProduct, PayChannel } from "@features/api/memberApi";
import { getAvailableChannels } from "@hooks/memberPlansData";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

/**
 * web 可下单渠道的展示与行为元数据（EDG-552）。
 *
 * 渠道**顺序、可用性与所属平台**只来自产品的 `payment_configs`（配置顺序即展示顺序，第一个为主按钮），
 * 这里只回答「这个渠道怎么展示 / 试用天数从哪读」。未登记的渠道只有后端下发了 `display_name` 才按通用跳转渠道
 * 展示（见 `resolveChannelMeta`），否则不出现在下单区。
 *
 * 渠道名以后端为准：产品配置填了 `display_name` 就用它（主渠道名、「Pay with {名称}」），这里登记的名称只是兜底；
 * 行为字段（试用天数来源、回跳是否主动确认）不受 `display_name` 影响。
 *
 * 文案是 thunk 而非字符串：模块级 `I18n.t(...)` 会在 locale 设置前求值而冻结为英文
 * （同 `screens/track/constants/shelfCardsConfig.ts` 的说明）。
 */
export interface ChannelMeta {
  /** 渠道名：主渠道名展示在次级入口前（「Airwallex · or Pay with PayPal」），不翻译 */
  channelName: () => string;
  /** 次级按钮文案（「or {label}」里的 label）；主按钮不带渠道名，只写渠道、不写卡等支付方式 */
  secondaryLabel: () => string;
  /** 订阅管理列表中的渠道名 */
  listLabel: () => string;
  /**
   * 渠道没有 offers 配置时，试用天数回退到商品目录 `effective_period`。
   *
   * STRIPE 的试用由渠道侧 offer 定义（`trial_period_days`），目录天数不代表实际计费，不能回退；
   * PAYPAL / AIRWALLEX 的试用由我方按目录天数编排（AIRWALLEX 见 be-design `startTrialSubscription`
   * 的 `trial_ends_at = now + currentEffectivePeriod`）。
   */
  catalogTrialFallback: boolean;
  /**
   * 托管页回跳后是否需要用暂存 session_id 主动 `/payment/confirm`（回跳 ≠ 生效）。
   * STRIPE / PAYPAL 沿用回跳即成功（渠道 webhook 兜底，不在本需求范围）。
   */
  requiresReturnConfirm: boolean;
}

export const CHANNEL_META: Partial<Record<PayChannel, ChannelMeta>> = {
  [PayChannel.AIRWALLEX]: {
    channelName: () => "Airwallex",
    secondaryLabel: () => I18n.t("Pay with Airwallex"),
    listLabel: () => I18n.t("Card"),
    catalogTrialFallback: true,
    requiresReturnConfirm: true,
  },
  [PayChannel.PAYPAL]: {
    channelName: () => "PayPal",
    secondaryLabel: () => I18n.t("Pay with PayPal"),
    listLabel: () => "PayPal",
    catalogTrialFallback: true,
    requiresReturnConfirm: false,
  },
  [PayChannel.STRIPE]: {
    channelName: () => "Stripe",
    secondaryLabel: () => I18n.t("Pay with card"),
    listLabel: () => "Stripe",
    catalogTrialFallback: false,
    requiresReturnConfirm: false,
  },
};

/** 后端配置的渠道名套进次级按钮文案 */
const payWithLabel = (displayName: string): string =>
  I18n.t("Pay with %{var0}", { var0: displayName });

/** 用后端 `display_name` 覆盖渠道名相关文案，行为字段保持不变 */
const withDisplayName = (
  meta: Omit<ChannelMeta, "channelName" | "secondaryLabel" | "listLabel">,
  displayName: string,
): ChannelMeta => ({
  ...meta,
  channelName: () => displayName,
  secondaryLabel: () => payWithLabel(displayName),
  listLabel: () => displayName,
});

/**
 * 后端新下发、前端未登记的渠道（EDG-552：新渠道接入不发前端）。
 *
 * 只在产品配置带 `display_name` 时启用，按钮文案为「Pay with {名称}」；下单走与已登记渠道相同的
 * `/payment/check` → 跳转收银台。后端不为它提供额外信息，因此一律保守处理：
 * 试用天数只认渠道 offers（列表接口不带 offers → 试用判为未就绪）、回跳不主动确认（等 webhook 生效）。
 */
const buildRedirectChannelMeta = (displayName: string): ChannelMeta =>
  withDisplayName(
    { catalogTrialFallback: false, requiresReturnConfirm: false },
    displayName,
  );

/** 产品配置里该渠道（ACTIVE）的 `display_name`；未配置 / 空白 → undefined */
const findDisplayName = (
  channel: string,
  product?: MemberProduct | null,
): string | undefined =>
  product?.payment_configs
    ?.find((config) => config.channel === channel && config.status === "ACTIVE")
    ?.display_name?.trim() || undefined;

/**
 * 已登记渠道取 `CHANNEL_META`，产品配置带 `display_name` 时渠道名以它为准；
 * 未登记但带 `display_name` 的按通用跳转渠道处理；否则不可下单
 */
export function resolveChannelMeta(
  channel: string,
  product?: MemberProduct | null,
): ChannelMeta | undefined {
  const registered = CHANNEL_META[channel as PayChannel];
  const displayName = findDisplayName(channel, product);
  if (registered) {
    return displayName ? withDisplayName(registered, displayName) : registered;
  }
  return displayName ? buildRedirectChannelMeta(displayName) : undefined;
}

/** 渠道名：产品配置的 `display_name`，没配时取前端登记的渠道名；不可下单 → undefined */
export function resolveChannelName(
  channel: string,
  product?: MemberProduct | null,
): string | undefined {
  return resolveChannelMeta(channel, product)?.channelName();
}

/** 回跳渠道是否需要主动确认；渠道参数来自 URL，未登记 / 缺省一律不确认（沿用回跳即成功） */
export function channelRequiresReturnConfirm(
  channel?: string | null,
): channel is PayChannel {
  return (
    !!channel && !!CHANNEL_META[channel as PayChannel]?.requiresReturnConfirm
  );
}

export interface CheckoutChannels {
  /** null → 「暂不支持支付」，主按钮禁用 */
  primary: PayChannel | null;
  secondary: PayChannel[];
}

/**
 * 第一个为主渠道，其余为次级（保留后端顺序）；只保留可下单的渠道（`resolveChannelMeta` 有结果）。
 *
 * `subscribedChannel`：用户当前生效订阅的渠道。后端跨渠道互斥（146087）只放行同渠道下单，
 * 所以该渠道仍在售时把它提到主按钮，老渠道用户点主按钮就能升级 / 续购；
 * 已不在售（不在列表里）时顺序不变，下单会被互斥拦截并提示先关闭原订阅。
 */
export function pickCheckoutChannels(
  product?: MemberProduct | null,
  subscribedChannel?: string | null,
): CheckoutChannels {
  if (!product) {
    return { primary: null, secondary: [] };
  }
  const channels = getAvailableChannels(product).filter(
    (channel) => resolveChannelMeta(channel, product) != null,
  );
  const ordered = channels.includes(subscribedChannel as PayChannel)
    ? [
        subscribedChannel as PayChannel,
        ...channels.filter((channel) => channel !== subscribedChannel),
      ]
    : channels;
  const [primary, ...secondary] = ordered;
  return primary ? { primary, secondary } : { primary: null, secondary: [] };
}
